import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faComments,
  faUsers,
  faUserCheck,
  faVideo,
  faHeadset
} from '@fortawesome/free-solid-svg-icons';
import './AIAutomation.css';

const AIAutomation = () => {
  const [expandedCards, setExpandedCards] = React.useState({});

  const toggleFeatures = (cardIndex) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardIndex]: !prev[cardIndex]
    }));
  };

  return (
    <motion.div 
      className="ai-automation-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <section className="consultations-section section">
        <div className="container">
          <div className="section-title">
            <h2>AI Automation Use Cases</h2>
            <p className="section-intro">
              Discover how our AI assistants can transform your business operations.
            </p>
          </div>

          <div className="consultation-grid">
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <h3>The AI Phone Agent</h3>
              <p>An AI with a human-sounding voice answers your calls 24/7. It can book appointments, answer common questions, and send follow-up texts or emails right after the call.</p>
            </div>
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faComments} />
              </div>
              <h3>The Smart WhatsApp Bot</h3>
              <p>An AI that instantly reads and understands customer messages on WhatsApp. It can answer product questions, check order statuses, and even create a support ticket in your system.</p>
            </div>
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h3>Your Internal Slack Expert</h3>
              <p>An AI assistant inside your team's Slack. Employees can ask it questions ("What's the status of Client X?" or "Summarise our support channel"), and it will find the answer and reply instantly.</p>
            </div>
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faUserCheck} />
              </div>
              <h3>AI Lead Qualifier & CRM Entry</h3>
              <p>When a new lead comes in from your website, the AI instantly scores them (Hot, Warm, or Cold), sends a personalized first email, and creates a perfect entry in your CRM (like HubSpot or Salesforce).</p>
            </div>
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faVideo} />
              </div>
              <h3>The AI Meeting Summariser</h3>
              <p>You upload a meeting recording (Zoom, Google Meet) to a folder. The AI "listens" to it, writes a clear summary with all action items, and posts it to the correct Slack channel or emails it to the team.</p>
            </div>
            <div className="consultation-box">
              <div className="consultation-icon">
                <FontAwesomeIcon icon={faHeadset} />
              </div>
              <h3>Smart Support Ticket Triage</h3>
              <p>The AI reads every new support email (or helpdesk ticket). It understands the urgency and topic (e.g., "Billing Issue," "Urgent Outage"), then automatically assigns it to the right person or team.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Automation solutions moved below Use Cases */}
      <section className="ai-automation section">
        <div className="container">
          <div className="section-title">
            <h2>AI Automation Solutions</h2>
            <p className="section-intro">
              Deploy voice, chat, and workflow assistants that keep your team focused on high-value work.
            </p>
          </div>

          <div className="ai-bots-section">
            <div className="pricing-container">
              {/* Level 1: Basic Web Embedded AI Bot */}
              <div className="pricing-card">
                <div className="pricing-badge">Level 1</div>
                <div className="pricing-title">Web Embedded AI Bot</div>
                <div className="pricing-description">
                  Perfect for websites needing intelligent customer interaction and support.
                </div>
                <div className="pricing-price">
                  <span className="price">Contact for pricing</span>
                </div>
                <button
                  className="features-toggle"
                  onClick={() => toggleFeatures(0)}
                >
                  Features <span className="toggle-icon">{expandedCards[0] ? '−' : '+'}</span>
                </button>
                <ul className={`pricing-features ${expandedCards[0] ? 'expanded' : ''}`}>
                  <li><span className="icon-check">✓</span> Understands natural language</li>
                  <li><span className="icon-check">✓</span> Can pull from company documents or website content</li>
                  <li><span className="icon-check">✓</span> Customizable tone and data</li>
                </ul>
                <a href="/contact" className="pricing-button">Get Started</a>
              </div>

              {/* Level 2: Intelligent AI Assistant */}
              <div className="pricing-card">
                <div className="pricing-badge secondary">Level 2</div>
                <div className="pricing-title">Intelligent AI Assistant</div>
                <div className="pricing-description">
                  Not just answering — but doing tasks. Advanced automation for your business.
                </div>
                <div className="pricing-price">
                  <span className="price">Contact for pricing</span>
                </div>
                <button
                  className="features-toggle"
                  onClick={() => toggleFeatures(1)}
                >
                  Features <span className="toggle-icon">{expandedCards[1] ? '−' : '+'}</span>
                </button>
                <ul className={`pricing-features ${expandedCards[1] ? 'expanded' : ''}`}>
                  <li><span className="icon-check">✓</span> Understands natural language</li>
                  <li><span className="icon-check">✓</span> Deployable on web, WhatsApp, Slack, and more</li>
                  <li><span className="icon-check">✓</span> Real-time interaction with live data</li>
                  <li><span className="icon-check">✓</span> Executes tasks via smart workflows</li>
                  <li><span className="icon-check">✓</span> Seamless API integrations</li>
                  <li><span className="icon-check">✓</span> Long-term memory for context</li>
                  <li><span className="icon-check">✓</span> Compatible with major business tools</li>
                </ul>
                <a href="/contact" className="pricing-button secondary-button">Get Started</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AIAutomation;
